import { useState, useEffect, useRef, useCallback } from "react";
import {
  Plus, Pencil, Trash2, Search, X, Check, AlertTriangle,
  Palette, Sparkles, Info, Package, ChevronRight, Home,
} from "lucide-react";

/* ─────────────────────────────────────────────
   SEED DATA
───────────────────────────────────────────── */
const INITIAL = {
  materials: [
    { id: 1, name: "Canvas",  active: true  },
    { id: 2, name: "Acrylic", active: false },
  ],
  colours: [
    { id: 1, name: "Crimson Red",   hex: "#DC143C", active: true  },
    { id: 2, name: "Royal Navy",    hex: "#001F5B", active: true  },
    { id: 3, name: "Emerald Green", hex: "#50C878", active: false },
  ],
  styles: [
    { id: 1, name: "Modern",   active: true  },
    { id: 2, name: "Vintage",  active: false },
    { id: 3, name: "Abstract", active: true  },
  ],
};

/* ─────────────────────────────────────────────
   TOAST
───────────────────────────────────────────── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

function ToastContainer({ toasts }) {
  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id}
          className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium border animate-slide-up
            ${t.type === "success" ? "bg-[#f0faf4] border-[#bbf7d0] text-[#1a6b3c]"
              : t.type === "error"  ? "bg-red-50 border-red-200 text-red-700"
              : "bg-amber-50 border-amber-200 text-amber-700"}`}>
          {t.type === "success" && <Check size={15} className="text-[#22A65A] flex-shrink-0" />}
          {t.type === "error"   && <X     size={15} className="text-red-500 flex-shrink-0" />}
          {t.type === "warning" && <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />}
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MODAL WRAPPER
───────────────────────────────────────────── */
function Modal({ open, onClose, children, maxW = "max-w-md" }) {
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxW} border border-[#e8e0d0] animate-modal-in`}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, icon: Icon, onClose }) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-[#f0ebe0]">
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="w-8 h-8 rounded-xl bg-[#f0faf4] flex items-center justify-center flex-shrink-0">
            <Icon size={16} className="text-[#22A65A]" />
          </div>
        )}
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
      </div>
      <button onClick={onClose}
        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
        <X size={16} />
      </button>
    </div>
  );
}

function ModalFooter({ onCancel, onConfirm, confirmLabel, confirmClass }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3 sm:justify-end px-6 py-4 border-t border-[#f0ebe0]">
      <button onClick={onCancel}
        className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 bg-white hover:bg-[#f7f3ec] transition-all active:scale-95">
        Cancel
      </button>
      <button onClick={onConfirm}
        className={`w-full sm:w-auto px-5 py-2.5 rounded-xl text-white text-sm font-medium transition-all active:scale-95 flex items-center justify-center gap-2 shadow-sm ${confirmClass || "bg-[#22A65A] hover:bg-[#1a8f4c]"}`}>
        {confirmLabel}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SHARED UI
───────────────────────────────────────────── */
function Field({ label, required, error, hint, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {hint  && !error && <p className="text-xs text-gray-400">{hint}</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function TextInput({ error, ...props }) {
  return (
    <input {...props}
      className={`w-full h-10 px-3 rounded-xl border text-sm bg-[#fdfcfa] text-gray-800 placeholder-gray-400 outline-none transition-all
        ${error ? "border-red-300 ring-2 ring-red-100"
          : "border-[#e2ddd4] hover:border-[#c5bfb5] focus:border-[#22A65A] focus:ring-2 focus:ring-[#22A65A]/15"}`}
    />
  );
}

function StatusRow({ active, onChange }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#e2ddd4] bg-[#fdfcfa]">
      <div>
        <p className="text-sm font-medium text-gray-700">Status</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {active ? "This item is currently active" : "This item is currently inactive"}
        </p>
      </div>
      <button type="button" onClick={onChange}
        className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200 flex-shrink-0
          ${active ? "bg-[#22A65A] border-[#1a8f4c]" : "bg-gray-200 border-gray-300"}`}>
        <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200
          ${active ? "translate-x-5" : "translate-x-1"}`} />
      </button>
    </div>
  );
}

function StatusToggle({ active, onChange }) {
  return (
    <button type="button" onClick={onChange}
      className={`relative inline-flex items-center h-5 w-9 rounded-full border transition-all duration-200 flex-shrink-0
        ${active ? "bg-[#22A65A] border-[#1a8f4c]" : "bg-gray-200 border-gray-300"}`}>
      <span className={`inline-block w-3.5 h-3.5 rounded-full bg-white shadow-sm transform transition-transform duration-200
        ${active ? "translate-x-4" : "translate-x-0.5"}`} />
    </button>
  );
}

function HexPicker({ hex, hexInput, onHexChange, onInputChange, error }) {
  const colorRef = useRef(null);
  const isValid  = (v) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v);
  return (
    <Field label="Hex Color Code" required error={error} hint="Click the swatch to open color picker">
      <div className="flex gap-2 items-center">
        <button type="button" onClick={() => colorRef.current?.click()}
          className="relative w-10 h-10 rounded-xl border-2 border-[#e2ddd4] hover:border-[#22A65A] transition-all flex-shrink-0 overflow-hidden cursor-pointer"
          style={{ backgroundColor: isValid(hex) ? hex : "#ccc" }}>
          <input ref={colorRef} type="color" value={isValid(hex) ? hex : "#22A65A"}
            onChange={(e) => onHexChange(e.target.value.toUpperCase())}
            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" />
        </button>
        <input type="text" value={hexInput} onChange={(e) => onInputChange(e.target.value)}
          placeholder="#FFFFFF" maxLength={7}
          className={`flex-1 h-10 px-3 rounded-xl border text-sm font-mono bg-[#fdfcfa] text-gray-800 uppercase outline-none transition-all
            ${error ? "border-red-300 ring-2 ring-red-100"
              : "border-[#e2ddd4] hover:border-[#c5bfb5] focus:border-[#22A65A] focus:ring-2 focus:ring-[#22A65A]/15"}`}
        />
        {isValid(hex) && (
          <div className="w-10 h-10 rounded-xl bg-[#f0faf4] border border-[#bbf7d0] flex items-center justify-center flex-shrink-0">
            <Check size={14} className="text-[#22A65A]" />
          </div>
        )}
      </div>
    </Field>
  );
}

/* ─────────────────────────────────────────────
   ADD MODAL
───────────────────────────────────────────── */
function AddModal({ open, onClose, onSave, section }) {
  const [name, setName]         = useState("");
  const [hex, setHex]           = useState("#22A65A");
  const [hexInput, setHexInput] = useState("#22A65A");
  const [active, setActive]     = useState(true);
  const [errors, setErrors]     = useState({});

  const isColour  = section?.id === "colours";
  const isValid   = (v) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v);

  useEffect(() => {
    if (open) { setName(""); setHex("#22A65A"); setHexInput("#22A65A"); setActive(true); setErrors({}); }
  }, [open]);

  const handleHexInput = (raw) => {
    setHexInput(raw);
    const v = raw.startsWith("#") ? raw : `#${raw}`;
    if (isValid(v)) { setHex(v.toUpperCase()); setErrors((p) => ({ ...p, hex: "" })); }
    else setErrors((p) => ({ ...p, hex: "Enter a valid hex (e.g. #FF5733)" }));
  };

  const handleSave = () => {
    const e = {};
    if (!name.trim()) e.name = `${section?.singular} name is required`;
    if (isColour && !isValid(hex)) e.hex = "Valid hex color required";
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ name: name.trim(), active, ...(isColour ? { hex: hex.toUpperCase() } : {}) });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title={`Add ${section?.singular}`} icon={Plus} onClose={onClose} />
      <div className="px-6 py-5 space-y-4">
        <Field label={`${section?.singular} Name`} required error={errors.name}>
          <TextInput placeholder={section?.placeholder} value={name} autoFocus
            onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: "" })); }}
            error={errors.name} />
        </Field>

        {isColour && (
          <HexPicker hex={hex} hexInput={hexInput} error={errors.hex}
            onHexChange={(v) => { setHex(v); setHexInput(v); setErrors((p) => ({ ...p, hex: "" })); }}
            onInputChange={handleHexInput} />
        )}

        <StatusRow active={active} onChange={() => setActive((v) => !v)} />
      </div>
      <ModalFooter onCancel={onClose} onConfirm={handleSave}
        confirmLabel={<><Check size={14} /> Save {section?.singular}</>}
        confirmClass={section?.btnClass} />
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   EDIT MODAL
───────────────────────────────────────────── */
function EditModal({ open, onClose, onSave, item, section }) {
  const [name, setName]         = useState("");
  const [hex, setHex]           = useState("#22A65A");
  const [hexInput, setHexInput] = useState("#22A65A");
  const [active, setActive]     = useState(true);
  const [errors, setErrors]     = useState({});

  const isColour = section?.id === "colours";
  const isValid  = (v) => /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(v);

  useEffect(() => {
    if (open && item) {
      setName(item.name || "");
      setHex(item.hex || "#22A65A");
      setHexInput(item.hex || "#22A65A");
      setActive(item.active !== false);
      setErrors({});
    }
  }, [open, item]);

  const handleHexInput = (raw) => {
    setHexInput(raw);
    const v = raw.startsWith("#") ? raw : `#${raw}`;
    if (isValid(v)) { setHex(v.toUpperCase()); setErrors((p) => ({ ...p, hex: "" })); }
    else setErrors((p) => ({ ...p, hex: "Enter a valid hex" }));
  };

  const handleSave = () => {
    const e = {};
    if (!name.trim()) e.name = "Name is required";
    if (isColour && !isValid(hex)) e.hex = "Valid hex required";
    if (Object.keys(e).length) { setErrors(e); return; }
    onSave({ ...item, name: name.trim(), active, ...(isColour ? { hex: hex.toUpperCase() } : {}) });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title={`Edit ${section?.singular}`} icon={Pencil} onClose={onClose} />
      <div className="px-6 py-5 space-y-4">
        <Field label={`${section?.singular} Name`} required error={errors.name}>
          <TextInput placeholder={section?.placeholder} value={name} autoFocus
            onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: "" })); }}
            error={errors.name} />
        </Field>

        {isColour && (
          <HexPicker hex={hex} hexInput={hexInput} error={errors.hex}
            onHexChange={(v) => { setHex(v); setHexInput(v); setErrors((p) => ({ ...p, hex: "" })); }}
            onInputChange={handleHexInput} />
        )}

        <StatusRow active={active} onChange={() => setActive((v) => !v)} />
      </div>
      <ModalFooter onCancel={onClose} onConfirm={handleSave}
        confirmLabel={<><Check size={14} /> Update {section?.singular}</>}
        confirmClass={section?.btnClass} />
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   DELETE MODAL
───────────────────────────────────────────── */
function DeleteModal({ open, onClose, onConfirm, item, section }) {
  return (
    <Modal open={open} onClose={onClose} maxW="max-w-sm">
      <div className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={22} className="text-red-500" />
        </div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Delete {section?.singular}?</h3>
        <p className="text-sm text-gray-500 mb-6">
          Are you sure you want to delete{" "}
          <span className="font-semibold text-gray-700">"{item?.name}"</span>?
          This action cannot be undone.
        </p>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all active:scale-95">
            Cancel
          </button>
          <button onClick={() => { onConfirm(item?.id); onClose(); }}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-all active:scale-95 flex items-center justify-center gap-2">
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   SECTION CONFIG
───────────────────────────────────────────── */
const SECTIONS = {
  materials: {
    id: "materials", title: "Materials", singular: "Material",
    placeholder: "e.g., Canvas, Vinyl, Leather",
    description: "Define the physical materials used in your products.",
    Icon: Package,
    iconBg: "bg-amber-50", iconColor: "text-amber-600",
    headerGrad: "from-amber-50 to-white",
    btnClass: "bg-amber-500 hover:bg-amber-600",
    addBtn: "bg-amber-500 hover:bg-amber-600",
    tagBg: "bg-amber-50", tagText: "text-amber-800", tagBorder: "border-amber-100",
    emptyBg: "bg-amber-50",
    accentBorder: "border-amber-200",
    statBg: "bg-amber-50",
    navActive: "bg-amber-500 text-white shadow-amber-200",
    navInactive: "text-amber-700 hover:bg-amber-50",
  },
  colours: {
    id: "colours", title: "Colours", singular: "Colour",
    placeholder: "e.g., Crimson Red, Ocean Blue",
    description: "Manage colour names and their corresponding hex codes.",
    Icon: Palette,
    iconBg: "bg-[#f0faf4]", iconColor: "text-[#22A65A]",
    headerGrad: "from-green-50 to-white",
    btnClass: "bg-[#22A65A] hover:bg-[#1a8f4c]",
    addBtn: "bg-[#22A65A] hover:bg-[#1a8f4c]",
    tagBg: "bg-[#f0faf4]", tagText: "text-[#1a6b3c]", tagBorder: "border-green-100",
    emptyBg: "bg-[#f0faf4]",
    accentBorder: "border-green-200",
    statBg: "bg-[#f0faf4]",
    navActive: "bg-[#22A65A] text-white shadow-green-200",
    navInactive: "text-[#22A65A] hover:bg-[#f0faf4]",
  },
  styles: {
    id: "styles", title: "Styles", singular: "Style",
    placeholder: "e.g., Modern, Vintage, Abstract",
    description: "Define visual styles and themes for your product catalogue.",
    Icon: Sparkles,
    iconBg: "bg-purple-50", iconColor: "text-purple-500",
    headerGrad: "from-purple-50 to-white",
    btnClass: "bg-purple-500 hover:bg-purple-600",
    addBtn: "bg-purple-500 hover:bg-purple-600",
    tagBg: "bg-purple-50", tagText: "text-purple-800", tagBorder: "border-purple-100",
    emptyBg: "bg-purple-50",
    accentBorder: "border-purple-200",
    statBg: "bg-purple-50",
    navActive: "bg-purple-500 text-white shadow-purple-200",
    navInactive: "text-purple-600 hover:bg-purple-50",
  },
};

/* ─────────────────────────────────────────────
   TABLE
───────────────────────────────────────────── */
function AttributeTable({ items, section, onEdit, onDelete, onToggle, search }) {
  const isColour = section.id === "colours";

  const filtered = items.filter((i) =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    (i.hex || "").toLowerCase().includes(search.toLowerCase())
  );

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <div className={`w-14 h-14 rounded-2xl ${section.emptyBg} flex items-center justify-center mb-4 border ${section.accentBorder}`}>
          <section.Icon size={26} className={section.iconColor} />
        </div>
        <p className="text-sm font-semibold text-gray-600 mb-1">No {section.title.toLowerCase()} yet</p>
        <p className="text-xs text-gray-400">Click "+ Add {section.singular}" above to get started</p>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center px-4">
        <Search size={22} className="text-gray-300 mb-2" />
        <p className="text-sm text-gray-400">No results for "<span className="font-medium">{search}</span>"</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#f0ebe0] bg-[#fdfcfa]">
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide w-10">#</th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Name</th>
            {isColour && (
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Swatch</th>
            )}
            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
            <th className="text-right px-5 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f5f0e8]">
          {filtered.map((item, idx) => (
            <tr key={item.id} className="hover:bg-[#fdfcfa] transition-colors group">
              <td className="px-5 py-4 text-xs text-gray-300 font-mono">{String(idx + 1).padStart(2, "0")}</td>
              <td className="px-5 py-4">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold
                  ${section.tagBg} ${section.tagText} border ${section.tagBorder}`}>
                  {item.name}
                </span>
              </td>
              {isColour && (
                <td className="px-5 py-4">
                  <div className="flex items-center gap-2.5">
                    <span className="w-7 h-7 rounded-lg border border-black/10 shadow-sm flex-shrink-0"
                      style={{ backgroundColor: item.hex }} />
                    <span className="text-xs font-mono text-gray-500">{item.hex?.toUpperCase()}</span>
                  </div>
                </td>
              )}
              <td className="px-5 py-4">
                <div className="flex items-center gap-2">
                  <StatusToggle active={item.active !== false} onChange={() => onToggle(item.id)} />
                  <span className={`text-[11px] font-semibold ${item.active !== false ? "text-[#22A65A]" : "text-gray-400"}`}>
                    {item.active !== false ? "Active" : "Inactive"}
                  </span>
                </div>
              </td>
              <td className="px-5 py-4">
                <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => onEdit(item)} title="Edit"
                    className="p-2 rounded-lg text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all active:scale-95">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => onDelete(item)} title="Delete"
                    className="p-2 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all active:scale-95">
                    <Trash2 size={13} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────
   INDIVIDUAL SECTION PAGE
───────────────────────────────────────────── */
function SectionPage({ section, items, toast }) {
  const [search, setSearch]     = useState("");
  const [addOpen, setAddOpen]   = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delItem,  setDelItem]  = useState(null);
  const [localItems, setLocalItems] = useState(items);

  // keep in sync when parent items change
  useEffect(() => setLocalItems(items), [items]);

  const filtered = localItems.filter((i) =>
    i.name.toLowerCase().includes(search.toLowerCase()) ||
    (i.hex || "").toLowerCase().includes(search.toLowerCase())
  );

  const activeCount   = localItems.filter((i) => i.active !== false).length;
  const inactiveCount = localItems.length - activeCount;

  const handleAdd = (fields) => {
    setLocalItems((p) => [...p, { id: Date.now(), ...fields }]);
    toast(`${section.singular} added successfully`);
  };

  const handleEdit = (updated) => {
    setLocalItems((p) => p.map((i) => (i.id === updated.id ? updated : i)));
    toast(`${section.singular} updated successfully`);
  };

  const handleDelete = (id) => {
    setLocalItems((p) => p.filter((i) => i.id !== id));
    toast(`${section.singular} deleted`, "warning");
  };

  const handleToggle = (id) => {
    setLocalItems((p) =>
      p.map((i) => (i.id === id ? { ...i, active: !(i.active !== false) } : i))
    );
  };

  return (
    <div className="space-y-5 animate-page-in">
      {/* ── Page title bar ── */}
      <div className={`bg-white rounded-2xl border ${section.accentBorder} shadow-sm overflow-hidden`}>
        <div className={`bg-gradient-to-r ${section.headerGrad} px-6 py-5 border-b border-[#f0ebe0]`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl ${section.iconBg} flex items-center justify-center flex-shrink-0 shadow-sm border ${section.accentBorder}`}>
                <section.Icon size={22} className={section.iconColor} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{section.title}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{section.description}</p>
              </div>
            </div>
            <button onClick={() => setAddOpen(true)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-semibold transition-all active:scale-95 shadow-sm ${section.addBtn}`}>
              <Plus size={15} /> Add {section.singular}
            </button>
          </div>
        </div>

        {/* ── Stat chips ── */}
        <div className="px-6 py-3 flex flex-wrap items-center gap-3 bg-white">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${section.statBg} border ${section.accentBorder}`}>
            <span className={`text-sm font-bold ${section.iconColor}`}>{localItems.length}</span>
            <span className="text-xs text-gray-500">Total</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#f0faf4] border border-green-100">
            <span className="w-2 h-2 rounded-full bg-[#22A65A]" />
            <span className="text-sm font-bold text-[#1a6b3c]">{activeCount}</span>
            <span className="text-xs text-gray-500">Active</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
            <span className="w-2 h-2 rounded-full bg-gray-300" />
            <span className="text-sm font-bold text-gray-500">{inactiveCount}</span>
            <span className="text-xs text-gray-500">Inactive</span>
          </div>
        </div>
      </div>

      {/* ── Table card ── */}
      <div className="bg-white rounded-2xl border border-[#e8e0d0] shadow-sm overflow-hidden">
        {/* Search bar */}
        {localItems.length > 0 && (
          <div className="px-5 py-3.5 border-b border-[#f5f0e8] flex items-center gap-3">
            <div className="relative flex-1">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${section.title.toLowerCase()}…`}
                className="w-full pl-8 pr-8 py-2 rounded-xl border border-[#e2ddd4] text-xs bg-[#fdfcfa] text-gray-700 placeholder-gray-400 outline-none focus:border-[#22A65A] focus:ring-2 focus:ring-[#22A65A]/10 transition-all"
              />
              {search && (
                <button onClick={() => setSearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                  <X size={12} />
                </button>
              )}
            </div>
            {search && (
              <span className="text-[11px] text-gray-400 flex-shrink-0">
                {filtered.length} / {localItems.length}
              </span>
            )}
          </div>
        )}

        <AttributeTable
          items={localItems}
          section={section}
          onEdit={setEditItem}
          onDelete={setDelItem}
          onToggle={handleToggle}
          search={search}
        />

        {localItems.length > 0 && (
          <div className="px-5 py-3 border-t border-[#f5f0e8] bg-[#fdfcfa] flex items-center justify-between">
            <span className="text-[11px] text-gray-400">
              Showing {filtered.length} of {localItems.length} {section.title.toLowerCase()}
            </span>
            {search && filtered.length < localItems.length && (
              <button onClick={() => setSearch("")}
                className="text-[11px] text-[#22A65A] hover:underline">
                Clear filter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddModal open={addOpen} onClose={() => setAddOpen(false)}
        onSave={handleAdd} section={section} />
      <EditModal open={!!editItem} onClose={() => setEditItem(null)}
        onSave={handleEdit} item={editItem} section={section} />
      <DeleteModal open={!!delItem} onClose={() => setDelItem(null)}
        onConfirm={handleDelete} item={delItem} section={section} />
    </div>
  );
}

/* ─────────────────────────────────────────────
   DASHBOARD  (overview)
───────────────────────────────────────────── */
function Dashboard({ data, onNavigate }) {
  return (
    <div className="space-y-6 animate-page-in">
      <div className="bg-white rounded-2xl border border-[#e8e0d0] shadow-sm px-6 py-5">
        <h2 className="text-base font-bold text-gray-800 mb-1">Overview</h2>
        <p className="text-xs text-gray-400">Select a section below to manage your product attributes.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {Object.values(SECTIONS).map((sec) => {
          const total    = data[sec.id]?.length || 0;
          const active   = (data[sec.id] || []).filter((i) => i.active !== false).length;
          const inactive = total - active;
          return (
            <button key={sec.id} onClick={() => onNavigate(sec.id)}
              className="bg-white rounded-2xl border border-[#e8e0d0] shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all text-left group overflow-hidden">
              <div className={`bg-gradient-to-br ${sec.headerGrad} px-5 py-4 border-b border-[#f0ebe0]`}>
                <div className="flex items-center justify-between">
                  <div className={`w-10 h-10 rounded-xl ${sec.iconBg} border ${sec.accentBorder} flex items-center justify-center`}>
                    <sec.Icon size={18} className={sec.iconColor} />
                  </div>
                  <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" />
                </div>
                <h3 className="text-base font-bold text-gray-800 mt-3">{sec.title}</h3>
                <p className="text-xs text-gray-400 mt-0.5">{sec.description}</p>
              </div>
              <div className="px-5 py-3 flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <span className="text-xl font-bold text-gray-800">{total}</span>
                  <span className="text-xs text-gray-400">total</span>
                </div>
                <div className="h-4 w-px bg-gray-200" />
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#22A65A]" />
                  <span className="text-xs text-gray-500">{active} active</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                  <span className="text-xs text-gray-500">{inactive} inactive</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SIDEBAR NAV
───────────────────────────────────────────── */
function Sidebar({ current, onChange, data }) {
  const pages = [
    { id: "dashboard", label: "Dashboard", Icon: Home },
    ...Object.values(SECTIONS).map((s) => ({ id: s.id, label: s.title, Icon: s.Icon, count: data[s.id]?.length || 0, sec: s })),
  ];

  return (
    <aside className="w-56 flex-shrink-0 hidden lg:flex flex-col gap-1">
      <div className="bg-white rounded-2xl border border-[#e8e0d0] shadow-sm p-3 space-y-0.5">
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-2 pb-2 pt-1">Navigation</p>
        {pages.map(({ id, label, Icon, count, sec }) => {
          const isActive = current === id;
          const colorClass = isActive
            ? (sec ? sec.navActive : "bg-gray-800 text-white shadow-gray-200")
            : (sec ? sec.navInactive : "text-gray-600 hover:bg-gray-100");
          return (
            <button key={id} onClick={() => onChange(id)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl text-sm font-medium transition-all shadow-sm ${colorClass}`}>
              <div className="flex items-center gap-2.5">
                <Icon size={15} />
                {label}
              </div>
              {count !== undefined && (
                <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md
                  ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </aside>
  );
}

/* ─────────────────────────────────────────────
   TOP TAB BAR  (mobile / tablet)
───────────────────────────────────────────── */
function TabBar({ current, onChange, data }) {
  const pages = [
    { id: "dashboard", label: "Home",      Icon: Home },
    { id: "materials", label: "Materials", Icon: Package,  count: data.materials?.length },
    { id: "colours",   label: "Colours",   Icon: Palette,  count: data.colours?.length   },
    { id: "styles",    label: "Styles",    Icon: Sparkles, count: data.styles?.length    },
  ];

  return (
    <div className="lg:hidden flex gap-1 bg-white border border-[#e8e0d0] rounded-2xl p-1.5 shadow-sm overflow-x-auto">
      {pages.map(({ id, label, Icon, count }) => {
        const sec = SECTIONS[id];
        const isActive = current === id;
        const activeClass = isActive
          ? (sec ? sec.navActive : "bg-gray-800 text-white")
          : "text-gray-500 hover:bg-gray-100";
        return (
          <button key={id} onClick={() => onChange(id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0 ${activeClass}`}>
            <Icon size={13} />
            {label}
            {count !== undefined && (
              <span className={`text-[10px] font-bold px-1 py-0.5 rounded ${isActive ? "bg-white/20" : "bg-gray-100 text-gray-500"}`}>
                {count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ─────────────────────────────────────────────
   BREADCRUMB
───────────────────────────────────────────── */
function Breadcrumb({ current }) {
  const sec = SECTIONS[current];
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-400">
      <Home size={11} />
      <span>Product Attributes</span>
      {sec && (
        <>
          <ChevronRight size={10} />
          <span className={`font-semibold ${sec.iconColor}`}>{sec.title}</span>
        </>
      )}
      {current === "dashboard" && (
        <>
          <ChevronRight size={10} />
          <span className="font-semibold text-gray-600">Dashboard</span>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   ROOT APP
───────────────────────────────────────────── */
export default function App() {
  const [page, setPage]   = useState("dashboard");
  const [data]            = useState(INITIAL);
  const { toasts, show: toast } = useToast();

  const currentSection = SECTIONS[page];

  return (
    <>
      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);   }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes pageIn {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0);   }
        }
        .animate-modal-in { animation: modalIn 0.18s ease-out both; }
        .animate-slide-up { animation: slideUp 0.2s ease-out both; }
        .animate-page-in  { animation: pageIn  0.2s ease-out both; }
      `}</style>

      <div className="min-h-screen bg-[#fff8ef] font-sans">
        {/* ── App Header ── */}
        <header className="bg-white border-b border-[#e8e0d0] sticky top-0 z-30 shadow-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-[#f0faf4] border border-[#bbf7d0] flex items-center justify-center">
                <Palette size={16} className="text-[#22A65A]" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-gray-900 leading-tight">Product Attributes</h1>
                <Breadcrumb current={page} />
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-400 bg-[#fdfcfa] border border-[#e8e0d0] rounded-xl px-3 py-2">
              <Info size={12} className="text-gray-300 flex-shrink-0" />
              <span className="hidden sm:inline">Manage product attribute data</span>
            </div>
          </div>
        </header>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
          {/* Tab bar (mobile) */}
          <div className="mb-5">
            <TabBar current={page} onChange={setPage} data={data} />
          </div>

          <div className="flex gap-6">
            {/* Sidebar (desktop) */}
            <Sidebar current={page} onChange={setPage} data={data} />

            {/* Main content */}
            <main className="flex-1 min-w-0">
              {page === "dashboard" ? (
                <Dashboard data={data} onNavigate={setPage} />
              ) : (
                <SectionPage
                  key={page}
                  section={currentSection}
                  items={data[page] || []}
                  toast={toast}
                />
              )}
            </main>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} />
    </>
  );
}